/**
 * Small browser-state hooks.
 *
 * `useLocalStorage` is the safe replacement for the legacy `safeParse` helper
 * plus the dozens of raw `localStorage.getItem(...)` / `JSON.parse` pairs — all
 * of which could and did throw on corrupt entries.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

function read(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw);
  } catch (err) {
    console.warn(`[Storage] Could not parse "${key}", using the fallback:`, err.message);
    return fallback;
  }
}

/**
 * State backed by localStorage, synchronised across tabs.
 * Setting `null` or `undefined` removes the entry.
 */
export function useLocalStorage(key, fallback) {
  const [value, setValue] = useState(() => read(key, fallback));

  // Re-read when the key itself changes (e.g. the signed-in user changed).
  const previousKey = useRef(key);
  useEffect(() => {
    if (previousKey.current !== key) {
      previousKey.current = key;
      setValue(read(key, fallback));
    }
    // `fallback` is deliberately excluded: callers commonly pass a fresh literal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const update = useCallback(
    (next) => {
      setValue((current) => {
        const resolved = typeof next === 'function' ? next(current) : next;
        try {
          if (resolved === null || resolved === undefined) localStorage.removeItem(key);
          else localStorage.setItem(key, JSON.stringify(resolved));
        } catch (err) {
          console.warn(`[Storage] Could not write "${key}":`, err.message);
        }
        return resolved;
      });
    },
    [key],
  );

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === key) setValue(read(key, fallback));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return [value, update];
}

/** sessionStorage variant, for values that should not outlive the tab. */
export function useSessionStorage(key, fallback) {
  const [value, setValue] = useState(() => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw === null ? fallback : JSON.parse(raw);
    } catch {
      return fallback;
    }
  });

  const update = useCallback(
    (next) => {
      setValue((current) => {
        const resolved = typeof next === 'function' ? next(current) : next;
        try {
          if (resolved === null || resolved === undefined) sessionStorage.removeItem(key);
          else sessionStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          /* ignore */
        }
        return resolved;
      });
    },
    [key],
  );

  return [value, update];
}

/** Delay a rapidly-changing value — used by the search fields. */
export function useDebounced(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/** Track a CSS media query, for the responsive sidebar. */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );

  useEffect(() => {
    const list = window.matchMedia(query);
    const onChange = (event) => setMatches(event.matches);
    setMatches(list.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** Close a popover when the user clicks outside it. */
export function useClickOutside(handler) {
  const ref = useRef(null);
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (ref.current && !ref.current.contains(event.target)) handlerRef.current(event);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, []);

  return ref;
}

/** Lock body scroll while a modal is open. */
export function useScrollLock(active) {
  useEffect(() => {
    if (!active) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [active]);
}

/** Stable id for label/aria wiring. */
export function useId(prefix = 'capsule') {
  return useMemo(() => `${prefix}-${Math.random().toString(36).slice(2, 9)}`, [prefix]);
}
